import { createContext, useContext, useState, useEffect } from 'react';
import { promptsService, variablesService } from '../services';
import { useAuth } from './AuthContext';

const PromptContext = createContext();

export const usePrompts = () => {
  const context = useContext(PromptContext);
  if (!context) {
    throw new Error('usePrompts must be used within a PromptProvider');
  }
  return context;
};

export const PromptProvider = ({ children }) => {
  const { user } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [variables, setVariables] = useState({});
  const [rawVariables, setRawVariables] = useState([]); // Store raw variable data with IDs
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadData();
    } else {
      setPrompts([]);
      setVariables({});
      setRawVariables([]);
      setLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [promptsData, variablesData] = await Promise.all([
        promptsService.getPrompts(),
        variablesService.getVariables()
      ]);
      setPrompts(promptsData);
      setRawVariables(variablesData); // Store raw data with IDs
      
      const normalizedVariables = {};
      variablesData.forEach((variable) => {
        if (!normalizedVariables[variable.key]) {
          normalizedVariables[variable.key] = [];
        }
        if (variable.value && variable.value.trim() !== '') {
          normalizedVariables[variable.key].push(variable.value);
        }
      });
      setVariables(normalizedVariables);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addPrompt = async (prompt) => {
    try {
      const newPrompt = await promptsService.createPrompt(prompt);
      setPrompts((prev) => [...prev, newPrompt]);
      return newPrompt.id;
    } catch (error) {
      console.error('Failed to add prompt:', error);
      throw error;
    }
  };

  const updatePrompt = async (id, updates) => {
    try {
      const updatedPrompt = await promptsService.updatePrompt(id, updates);
      setPrompts((prev) =>
        prev.map((prompt) => (prompt.id === id ? updatedPrompt : prompt))
      );
    } catch (error) {
      console.error('Failed to update prompt:', error);
      throw error;
    }
  };

  const deletePrompt = async (id) => {
    try {
      await promptsService.deletePrompt(id);
      setPrompts((prev) => prev.filter((prompt) => prompt.id !== id));
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      throw error;
    }
  };

  const deleteMultiplePrompts = async (ids) => {
    try {
      await promptsService.deleteMultiplePrompts(ids);
      setPrompts((prev) => prev.filter((prompt) => !ids.includes(prompt.id)));
    } catch (error) {
      console.error('Failed to delete prompts:', error);
      throw error;
    }
  };

  const getPrompt = (id) => {
    return prompts.find((prompt) => prompt.id === id);
  };

  const togglePromptSelection = (id) => {
    setSelectedPrompts((prev) =>
      prev.includes(id) ? prev.filter((promptId) => promptId !== id) : [...prev, id]
    );
  };

  const clearSelection = () => {
    setSelectedPrompts([]);
  };

  const addVariable = async (key, values = [], favourite = false) => {
    try {
      const normalizedKey = key.trim();
      if (!normalizedKey) return;

      if (!values || values.length === 0) {
        await variablesService.createVariable({ key: normalizedKey, value: "", favourite });
      } else {
        for (const value of values) {
          await variablesService.createVariable({ key: normalizedKey, value, favourite });
        }
      }

      await loadData();
    } catch (error) {
      console.error('Failed to add variable:', error);
      throw error;
    }
  };

  const toggleVariableFavourite = async (id, favourite) => {
    const previousRawVariables = [...rawVariables];
    
    try {
      setRawVariables(prev => 
        prev.map(v => v.id === id ? { ...v, favourite } : v)
      );
      
      await variablesService.updateFavourite(id, favourite);
    } catch (error) {
      setRawVariables(previousRawVariables);
      console.error('Failed to toggle variable favourite:', error);
      throw error;
    }
  };

  const toggleVariableFavouriteByKey = async (key, favourite) => {
    const previousKeyVariables = rawVariables
      .filter((variable) => variable.key === key)
      .map((variable) => ({ id: variable.id, favourite: variable.favourite }));

    if (previousKeyVariables.length === 0) return;

    try {
      setRawVariables((prev) =>
        prev.map((variable) =>
          variable.key === key ? { ...variable, favourite } : variable
        )
      );

      await Promise.all(
        previousKeyVariables.map((variable) =>
          variablesService.updateFavourite(variable.id, favourite)
        )
      );
    } catch (error) {
      setRawVariables((prev) =>
        prev.map((variable) => {
          if (variable.key !== key) return variable;

          const previous = previousKeyVariables.find(
            (item) => item.id === variable.id
          );

          return previous ? { ...variable, favourite: previous.favourite } : variable;
        })
      );
      console.error('Failed to toggle variable favourite:', error);
      throw error;
    }
  };

  const deleteVariable = async (key) => {
    try {
      // Find all variables with this key and delete them by their real IDs
      const variablesToDelete = rawVariables.filter(v => v.key === key);
      for (const variable of variablesToDelete) {
        try {
          await variablesService.deleteVariable(variable.id);
        } catch (e) {
          // Ignore individual delete errors
        }
      }
      await loadData();
    } catch (error) {
      console.error('Failed to delete variable:', error);
      throw error;
    }
  };

  const addValueToVariable = async (key, value) => {
    try {
      if (!variables[key]) {
        await addVariable(key, [value]);
      } else if (!variables[key].includes(value)) {
        await variablesService.createVariable({ key, value });
        await loadData();
      }
    } catch (error) {
      console.error('Failed to add value to variable:', error);
      throw error;
    }
  };

  const removeValueFromVariable = async (key, value) => {
    try {
      // Find the variable with this key and value to get its real ID
      const variableToRemove = rawVariables.find(v => v.key === key && v.value === value);
      if (variableToRemove) {
        await variablesService.deleteVariable(variableToRemove.id);
        await loadData();
      }
    } catch (error) {
      console.error('Failed to remove value from variable:', error);
      throw error;
    }
  };

  const value = {
    prompts,
    selectedPrompts,
    variables,
    rawVariables,
    loading,
    addPrompt,
    updatePrompt,
    deletePrompt,
    deleteMultiplePrompts,
    getPrompt,
    togglePromptSelection,
    clearSelection,
    addVariable,
    deleteVariable,
    toggleVariableFavourite,
    toggleVariableFavouriteByKey,
    addValueToVariable,
    removeValueFromVariable,
    refreshData: loadData,
  };

  return <PromptContext.Provider value={value}>{children}</PromptContext.Provider>;
};
